/**
 * GDSUrlManager.js
 * Handles building a shareable URL and applying selections from it.
 */
export class GDSUrlManager {
    constructor(builder) { // Renamed 'manager' to 'builder' for clarity
        this.builder = builder;
        // It's better to get specific managers from the builder
        this.state = builder.stateManager;
        this.api = builder.apiService;
        this.ui = builder.ui; // Assuming GDSCardUI is on the builder
        this.dataService = builder.cardManager.dataService; // Accessing nested service
        this.uiFeedback = builder.uiFeedback;
    }

    /**
     * Creates a snapshot for the current state and updates the browser URL.
     * This orchestrates the entire process.
     * @param {boolean} [useReplace=false] - True to use history.replaceState.
     * @param {boolean} [isSelectionChangeOnly=false] - True if only card selections changed.
     */
    async updateURLWithNewSnapshotId(useReplace = false, isSelectionChangeOnly = false) {
        if (!this.state.shouldCreateSnapshot()) {
            console.warn("[UrlManager] Conditions not met to create snapshot. URL not updated.");
            return;
        }

        try {
            const configuration = this.state.getCurrentConfigurationForSnapshot(
                () => this.buildSelectionsForURL()
            );

            if (!configuration) {
                this.uiFeedback.showError("Cannot create a shareable link without a valid configuration.");
                return;
            }

            const snapshotResponse = await this.api.createSnapshot(configuration);

            if (snapshotResponse && snapshotResponse.short_id) {
                // If it's just a selection change, ALWAYS replace the history state.
                // Otherwise, respect the 'useReplace' flag passed to the function.
                const actualUseReplace = useReplace || isSelectionChangeOnly;

                this.state.updateUrlWithSnapshot(snapshotResponse.short_id, snapshotResponse.snapshot_id, actualUseReplace);
            } else {
                console.warn("[UrlManager] Failed to get snapshot ID from API. URL not updated.");
            }

        } catch (error) {
            console.error("Error in updateURLWithNewSnapshotId:", error);
            this.uiFeedback.showError("An error occurred while creating the shareable link.");
        }
    }

    buildSelectionsForURL() {
        const selections = { main_cards: {}, additional_cards: {} };
        // Use this.builder.contentContainer for consistency
        if (!this.builder.contentContainer) return selections;

        this.builder.contentContainer.querySelectorAll('.product-card[data-card-index="0"]').forEach(card => {
            const cardTitle = card.dataset.cardTitle;
            const cardSelections = this.getCardSelectionsForURL(card);
            const containerKey = card.closest('#additional-cards-container') ? 'additional_cards' : 'main_cards';
            if (cardTitle) {
                selections[containerKey][cardTitle] = cardSelections;
            }
        });
        return selections;
    }

    async applySelectionsFromURL(selections) {
        if (!selections || this.state.currentStep !== 3) return;

        this.uiFeedback.showLoading();
        try {
            const { main_cards, additional_cards } = selections;

            if (main_cards) {
                const container = this.builder.contentContainer.querySelector('#main-cards-container');
                await this.applyCardSelections(main_cards, container, true);
            }
            if (additional_cards) {
                const container = this.builder.contentContainer.querySelector('#additional-cards-container');
                await this.applyCardSelections(additional_cards, container, false);
            }
            this.builder.cardManager.ui.updateSubtotal();
        } catch (error) {
            console.error("Error applying selections from URL:", error);
            this.uiFeedback.showError("Could not apply the shared configuration.");
        } finally {
            this.uiFeedback.hideLoading();
        }
    }

    async applyCardSelections(selectionsMap, container, isMainGroup) {
        if (!container) return;
        const cardsToKeep = new Set(Object.keys(selectionsMap));

        // Remove unneeded cards
        container.querySelectorAll('.product-card[data-card-index="0"]').forEach(card => {
            if (!cardsToKeep.has(card.dataset.cardTitle)) {
                this.builder.cardManager.ui.removeCardGroup(card);
            }
        });

        // Update or recreate needed cards
        for (const [cardTitle, cardSelections] of Object.entries(selectionsMap)) {
            let mainCard = container.querySelector(`.product-card[data-card-title="${cardTitle}"]`);
            if (!mainCard) {
                const cardData = this.dataService.getCardDataByTitle(cardTitle);
                if (cardData) {
                    mainCard = await this.recreateAndAddCardGroup(cardTitle, cardData, container, isMainGroup);
                }
            }
            if (mainCard) {
                const { product_handle, selected, secondary_cards, ...options } = cardSelections;
                await this.dataService.fetchAndUpdateCardData(mainCard, product_handle, options);
                this.builder.cardManager.ui.setCardSelectedState(mainCard, selected);
                if (secondary_cards?.length > 0) {
                    await this.builder.cardManager.ui.handleAdditionalProducts(mainCard, secondary_cards.map(sc => sc.product_handle));
                } else {
                    this.builder.cardManager.ui.removeSecondaryCards(mainCard);
                }
            }
        }
    }

    getCardSelectionsForURL(card) {
        const selections = JSON.parse(card.dataset.selections || '{}');
        const cleanSelections = {};
        Object.keys(selections).forEach(key => {
            if (!key.startsWith('_')) {
                cleanSelections[key] = selections[key];
            }
        });
        cleanSelections.product_handle = card.dataset.productHandle;
        cleanSelections.selected = card.dataset.selected === 'true';
        const secondaryInfo = this.getSecondaryCardsInfo(card);
        if (secondaryInfo) {
           cleanSelections.secondary_cards = secondaryInfo;
        }
        return cleanSelections;
    }

    getSecondaryCardsInfo(mainCard) {
        if (!mainCard) return null;
        const cardTitle = mainCard.getAttribute('data-card-title');
        const container = mainCard.closest('#main-cards-container, #additional-cards-container');
        if (!container || !cardTitle) return null;

        const selector = `.product-card[data-card-title="${cardTitle}"][data-secondary-index]:not([data-secondary-index="0"])`;
        const secondaryCards = Array.from(container.querySelectorAll(selector));

        if (secondaryCards.length === 0) return null;

        return secondaryCards.map(card => ({
            product_handle: card.getAttribute('data-product-handle'),
            selected: card.hasAttribute('data-selected'),
            index: card.getAttribute('data-secondary-index')
        }));
    }

    async recreateAndAddCardGroup(cardTitle, cardData, targetContainer, isMainCardGroup) {
        const cardGroupHTML = isMainCardGroup
            ? await this.dataService.processMainCardGroup(cardTitle, cardData)
            : await this.dataService.processAdditionalCardGroup(cardTitle, cardData, this.dataService.getPropagatingValuesFromRenderedMainCards());

        if (cardGroupHTML && targetContainer) {
            targetContainer.insertAdjacentHTML('beforeend', cardGroupHTML);
            const newCard = targetContainer.querySelector(`.product-card[data-card-title="${cardTitle}"]`);
            this.builder.eventHandler.setupSingleCardEventListeners(newCard);
            this.builder.modalManager.setupAllCardModalListeners(); // Re-scan
            return newCard;
        }
        return null;
    }
}
